<template>
  <div class="w-full h-full flex flex-col overflow-hidden bg-dark-secondary">
    <div class="max-w-[1600px] mx-auto w-full px-6 lg:px-8 py-6 lg:py-8 flex flex-col flex-1 min-h-0 gap-0">

      <div class="flex items-center justify-between mb-8">
        <div>
          <h1 class="text-2xl font-bold text-dark-primary tracking-tight">Recordings</h1>
          <p class="text-sm text-dark-secondary mt-1">Upload and monitor session processing pipeline</p>
        </div>
        <button
          @click="showUpload = !showUpload"
          class="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition shadow-sm"
          :class="showUpload ? 'bg-dark-tertiary border border-dark text-dark-primary hover:bg-dark-quaternary' : 'bg-accent text-white hover:opacity-90'"
        >
          <Upload class="w-4 h-4" />
          {{ showUpload ? 'Cancel' : 'Upload' }}
        </button>
        <input ref="fileInput" type="file" accept="*" multiple class="hidden" @change="onFileSelected" />
      </div>

      <Transition
        enter-active-class="transition-all duration-200 ease-out"
        enter-from-class="opacity-0 -translate-y-2"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition-all duration-150 ease-in"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-2"
      >
        <div v-if="showUpload" class="mb-6">
          <div
            class="border-2 border-dashed rounded-xl p-8 flex flex-col items-center gap-3 transition-colors"
            :class="isDragging ? 'border-accent bg-accent/5' : 'border-dark bg-dark-primary hover:border-accent/50'"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="onDrop"
          >
            <div class="w-10 h-10 rounded-full bg-dark-tertiary border border-dark flex items-center justify-center">
              <Upload class="w-5 h-5 text-dark-secondary" />
            </div>
            <div class="text-sm text-dark-primary font-medium">Drag & drop session files here</div>
            <div class="text-xs text-dark-secondary">
              or <button @click="fileInput?.click()" class="text-accent hover:underline">browse files</button>
              — multiple files supported
            </div>
          </div>
          <div v-if="uploads.length" class="mt-3 space-y-2">
            <div v-for="u in uploads" :key="u.id" class="flex items-center gap-4 p-4 bg-dark-primary border border-dark rounded-xl">
              <FileAudio class="w-5 h-5 text-accent flex-shrink-0" />
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-dark-primary truncate">{{ u.filename }}</p>
                <div class="w-full h-1.5 bg-dark-quaternary rounded-full mt-2 overflow-hidden">
                  <div class="h-full bg-accent transition-all duration-300" :style="{ width: u.progress + '%' }"></div>
                </div>
              </div>
              <span class="text-xs text-dark-secondary font-semibold flex-shrink-0">{{ u.progress }}%</span>
              <Loader v-if="u.progress < 100" class="w-4 h-4 text-accent animate-spin flex-shrink-0" />
              <CheckCircle v-else class="w-4 h-4 text-success flex-shrink-0" />
            </div>
          </div>
        </div>
      </Transition>

      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div class="bg-dark-primary border border-dark rounded-xl p-5 shadow-sm flex flex-col justify-center">
          <p class="text-[11px] text-dark-secondary font-bold uppercase tracking-wider mb-2">Total</p>
          <p class="text-3xl font-bold text-dark-primary">{{ stats.total }}</p>
        </div>
        <div class="bg-dark-primary border border-dark rounded-xl p-5 shadow-sm flex flex-col justify-center">
          <p class="text-[11px] text-dark-secondary font-bold uppercase tracking-wider mb-2">Completed</p>
          <p class="text-3xl font-bold text-success">{{ stats.completed }}</p>
        </div>
        <div class="bg-dark-primary border border-dark rounded-xl p-5 shadow-sm flex flex-col justify-center">
          <p class="text-[11px] text-dark-secondary font-bold uppercase tracking-wider mb-2">Processing</p>
          <p class="text-3xl font-bold text-warning">{{ stats.processing }}</p>
        </div>
        <div class="bg-dark-primary border border-dark rounded-xl p-5 shadow-sm flex flex-col justify-center">
          <p class="text-[11px] text-dark-secondary font-bold uppercase tracking-wider mb-2">Failed</p>
          <p class="text-3xl font-bold text-error">{{ stats.failed }}</p>
        </div>
      </div>

      <!-- SEARCH -->
      <div class="search-local-wrapper mb-6">
        <Search class="search-local-icon" />
        <input
          v-model="localSearch"
          type="text"
          placeholder="Search by filename, session ID or protocol…"
          class="search-local-input"
          autocomplete="off"
          spellcheck="false"
        />
        <button
          v-if="localSearch"
          @click="localSearch = ''"
          class="search-clear-btn"
          title="Clear"
        >
          <X class="w-3.5 h-3.5" />
        </button>
      </div>

      <div class="flex items-center gap-2 mb-4">
        <div class="flex items-center gap-1 flex-1 min-w-0">
          <button @click="loadSessions" class="toolbar-btn" title="Reload">
            <RefreshCw class="w-4 h-4" />
          </button>
          <template v-if="selectedSessions.length > 0">
            <div class="w-px h-5 bg-dark mx-1"></div>
            <span class="text-xs text-accent font-semibold px-2">{{ selectedSessions.length }} selected</span>
            <button class="toolbar-btn" title="Delete selected" @click="deleteSelected">
              <Trash2 class="w-4 h-4 hover:text-error transition-colors" />
            </button>
          </template>
          <template v-else>
            <div class="w-px h-5 bg-dark mx-1"></div>
            <div class="flex overflow-hidden">
              <button
                v-for="tab in tabs" :key="tab.value"
                class="tab-btn"
                :class="{ active: activeTab === tab.value }"
                @click="activeTab = tab.value"
              >{{ tab.label }}</button>
            </div>
          </template>
        </div>
        <div class="flex items-center gap-1 flex-shrink-0">
          <span class="text-xs text-dark-secondary px-2 whitespace-nowrap">
            {{ filtered.length }} de {{ totalCount }}
          </span>
          <button class="toolbar-btn" :disabled="page === 0" @click="prevPage"><ChevronLeft class="w-4 h-4" /></button>
          <button class="toolbar-btn" :disabled="pageEnd >= totalCount" @click="nextPage"><ChevronRight class="w-4 h-4" /></button>
        </div>
      </div>

      <div class="flex-1 min-h-0 flex flex-col bg-dark-primary border border-dark rounded-xl shadow-sm overflow-hidden">

        <div v-if="loading" class="flex items-center justify-center py-20">
          <div class="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
        </div>

        <div v-else-if="!filtered.length" class="flex-1 flex flex-col items-center justify-center text-center">
          <HardDrive class="w-12 h-12 text-dark-secondary opacity-40 mb-4" />
          <p class="text-sm font-medium text-dark-secondary mb-1">{{ localSearch ? 'No recordings match your search' : 'No recordings found' }}</p>
          <p class="text-xs text-dark-tertiary">{{ localSearch ? 'Try a different search term' : 'Upload a session file to get started' }}</p>
        </div>

        <template v-else>
          <div class="flex-1 overflow-auto custom-scrollbar">
            <table class="w-full min-w-[1000px] border-collapse">
              <thead class="sticky top-0 z-10 bg-dark-primary/95 backdrop-blur-sm">
                <tr class="border-b border-dark">
                  <th class="table-th w-8 text-center">
                    <input type="checkbox" v-model="selectAll" class="w-3.5 h-3.5 cursor-pointer accent-accent" />
                  </th>
                  <th class="table-th w-20">Session</th>
                  <th class="table-th">Session ID</th>
                  <th class="table-th w-24">Size</th>
                  <th class="table-th w-24">Duration</th>
                  <th class="table-th w-20">Protocol</th>
                  <th class="table-th w-[280px]">Pipeline</th>
                  <th class="table-th w-44">Uploaded</th>
                  <th class="table-th w-16"></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="session in filtered" :key="session.id"
                  class="border-b border-dark transition group"
                  :class="[
                    selectedSessions.includes(session.id) ? 'bg-accent/5' : 'hover:bg-dark-secondary/50',
                    session.status === 'failed' ? 'bg-error/5' : ''
                  ]"
                >
                  <td class="table-td text-center" @click.stop>
                    <input
                      type="checkbox" :value="session.id" v-model="selectedSessions"
                      class="w-3.5 h-3.5 cursor-pointer accent-accent transition-opacity"
                      :class="selectedSessions.includes(session.id) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'"
                    />
                  </td>
                  <td class="table-td max-w-[80px]">
                    <p class="text-sm font-medium text-dark-primary truncate">{{ session.filename || 'Unknown' }}</p>
                  </td>
                  <td class="table-td max-w-0">
                    <p class="text-sm text-dark-secondary font-mono truncate" :title="session.id">
                      {{ session.id }}
                    </p>
                  </td>
                  <td class="table-td">
                    <span class="text-sm text-dark-secondary font-tabular">{{ formatBytes(session.file_size) }}</span>
                  </td>
                  <td class="table-td">
                    <span class="text-sm text-dark-secondary font-tabular">{{ formatDuration(session.guac?.duration_ms) }}</span>
                  </td>
                  <td class="table-td">
                    <span class="session-tag" :class="'tag-' + protocolLabel(session).toLowerCase()">{{ protocolLabel(session) }}</span>
                  </td>
                  <td class="table-td">
                    <div class="flex flex-col gap-1.5">
                      <div class="flex gap-1 items-center w-[240px]">
                        <div
                          v-for="item in pipelineStages(session)" :key="item.key"
                          class="h-[8px] min-h-[8px] flex-1 rounded-full transition-all duration-500"
                          :style="stageColor(item.stage)"
                          :title="stageTitle(item)"
                        ></div>
                      </div>
                      <p class="text-[11px] font-medium" :style="pipelineStatusStyle(session)">
                        {{ pipelineStatusLabel(session) }}
                      </p>
                    </div>
                  </td>
                  <td class="table-td whitespace-nowrap">
                    <span class="text-sm text-dark-secondary font-tabular">{{ formatDate(session.uploaded_at) }}</span>
                  </td>
                  <td class="table-td" @click.stop>
                    <div class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        v-if="session.status === 'failed' || session.status === 'completed'"
                        @click="reprocess(session)" class="action-btn" title="Reprocess"
                      >
                        <RefreshCw class="w-3 h-3" />
                      </button>
                      <button @click="deleteSession(session)" class="action-btn danger" title="Delete">
                        <Trash2 class="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import {
  Upload, HardDrive, FileAudio, Loader, CheckCircle,
  ChevronLeft, ChevronRight, RefreshCw, Trash2, Search, X,
} from "lucide-vue-next";
import { apiClient } from "../services/apiClient";
import { useSessionStore } from "../stores/sessionStore";
import { useAuthStore }    from "../stores/authStore";

const sessionStore = useSessionStore();
const authStore    = useAuthStore();

// ─── Upload ───────────────────────────────────────────────────────────────────
const showUpload = ref(false);
const fileInput  = ref(null);
const uploads    = ref([]);
const isDragging = ref(false);

function onFileSelected(e) {
  const files = [...e.target.files];
  if (files.length) uploadFiles(files);
  e.target.value = "";
}

function onDrop(e) {
  isDragging.value = false;
  const files = [...e.dataTransfer.files];
  if (files.length) uploadFiles(files);
}

async function uploadFiles(files) {
  for (const file of files) {
    const upload = { id: Date.now() + Math.random(), filename: file.name, progress: 0 };
    uploads.value.push(upload);
    try {
      const form    = new FormData();
      form.append("file", file);
      const xhr     = new XMLHttpRequest();
      const apiKey  = authStore.apiKey;
      const baseUrl = authStore.apiBase;
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) upload.progress = Math.round((e.loaded / e.total) * 100);
      };
      await new Promise((resolve, reject) => {
        xhr.onload  = () => (xhr.status < 300 ? resolve() : reject(new Error(xhr.responseText)));
        xhr.onerror = () => reject(new Error("Network error"));
        xhr.open("POST", `${baseUrl}/sessions`);
        xhr.setRequestHeader("X-API-Key", apiKey);
        xhr.send(form);
      });
      upload.progress = 100;
      setTimeout(() => {
        uploads.value = uploads.value.filter((u) => u.id !== upload.id);
        if (!uploads.value.length) showUpload.value = false;
        loadSessions();
      }, 1500);
    } catch (err) {
      console.error("[Recordings] Upload failed:", file.name, err);
      uploads.value = uploads.value.filter((u) => u.id !== upload.id);
    }
  }
}

// ─── Session list ─────────────────────────────────────────────────────────────
const loading         = ref(true);
const activeTab       = ref("all");
const localSearch     = ref("");
const page            = ref(0);
const pageSize        = 50;
const stageUpdateTick = ref(0);

const selectedSessions = ref([]);
const selectAll = computed({
  get() { return filtered.value.length > 0 && selectedSessions.value.length === filtered.value.length; },
  set(val) { selectedSessions.value = val ? filtered.value.map((s) => s.id) : []; },
});

const pageStart = computed(() => Math.min(page.value * pageSize + 1, totalCount.value || 1));
const pageEnd   = computed(() => Math.min((page.value + 1) * pageSize, totalCount.value));

function prevPage() { if (page.value > 0) { page.value--; } }
function nextPage() { if (pageEnd.value < totalCount.value) { page.value++; } }

const tabs = [
  { label: "ALL",        value: "all"        },
  { label: "PROCESSING", value: "processing" },
  { label: "COMPLETED",  value: "completed"  },
  { label: "FAILED",     value: "failed"     },
];

watch(activeTab, () => { page.value = 0; selectedSessions.value = []; });
watch(localSearch, () => { page.value = 0; selectedSessions.value = []; });

async function loadSessions() {
  loading.value = true;
  try {
    await sessionStore.loadSessions();
  } catch (err) {
    console.error("[Recordings]", err);
  } finally {
    loading.value = false;
  }
}

const filtered = computed(() => {
  stageUpdateTick.value;
  const all = sessionStore.sessions;

  let result = all;
  if (activeTab.value === "processing") result = result.filter((s) => s.status === "processing" || s.status === "uploaded");
  else if (activeTab.value === "completed")  result = result.filter((s) => s.status === "completed");
  else if (activeTab.value === "failed")     result = result.filter((s) => s.status === "failed");

  const q = localSearch.value.trim().toLowerCase();
  if (q) {
    result = result.filter((s) =>
      (s.filename ?? "").toLowerCase().includes(q) ||
      s.id.toLowerCase().includes(q) ||
      protocolLabel(s).toLowerCase().includes(q)
    );
  }

  return result;
});

const totalCount = computed(() => sessionStore.sessionsTotal || sessionStore.sessions.length);

const stats = computed(() => {
  // Use the full list for status breakdown — these are accurate per current page
  // but total comes from API to avoid pagination truncation
  const all = sessionStore.sessions;
  return {
    total:      sessionStore.sessionsTotal || all.length,
    completed:  all.filter((s) => s.status === "completed").length,
    processing: all.filter((s) => s.status === "processing" || s.status === "uploaded").length,
    failed:     all.filter((s) => s.status === "failed").length,
  };
});

// ─── Actions ──────────────────────────────────────────────────────────────────
async function reprocess(session) {
  try {
    await apiClient(`/detections/run?session_id=${session.id}`, { method: "POST" });
    await loadSessions();
  } catch (err) {
    console.error("[Recordings] Reprocess failed:", err);
  }
}

async function deleteSession(session) {
  if (!confirm(`Delete session "${session.filename}"?\nThis cannot be undone.`)) return;
  try {
    await apiClient(`/sessions/${session.id}`, { method: "DELETE" });
    await loadSessions();
  } catch (err) {
    console.error("[Recordings] Delete failed:", err);
  }
}

async function deleteSelected() {
  if (!selectedSessions.value.length) return;
  if (!confirm(`Delete ${selectedSessions.value.length} session(s)? This cannot be undone.`)) return;
  for (const id of selectedSessions.value) {
    try { await apiClient(`/sessions/${id}`, { method: "DELETE" }); } catch {}
  }
  selectedSessions.value = [];
  await loadSessions();
}

// ─── Pipeline helpers ─────────────────────────────────────────────────────────
function pipelineStages(session) {
  return [
    { key: "guac", label: "GUAC", stage: session.guac },
    { key: "hls",  label: "HLS",  stage: session.hls  },
    { key: "ocr",  label: "OCR",  stage: session.ocr  },
    { key: "vlm",  label: "VLM",  stage: session.vlm  },
    { key: "llm",  label: "LLM",  stage: session.llm  },
  ];
}

function stageColor(stage) {
  if (!stage) return "background: rgba(128,128,128,0.2)";
  if (stage.status === "failed") return "background: #c25f5f";
  if (stage.status === "completed") return "background: var(--accent)";
  if (stage.status === "started" || stage.status === "processing") return "background: #c8a84b";
  return "background: rgba(128,128,128,0.2)";
}

function stageTitle(item) {
  if (!item.stage) return `${item.label}: queued`;
  if (item.stage.status === "failed") return `${item.label}: failed`;
  if (item.stage.status === "completed") return `${item.label}: completed`;
  if (item.stage.status === "started" || item.stage.status === "processing") return `${item.label}: processing`;
  return `${item.label}: queued`;
}

function pipelineStatusStyle(session) {
  if (session.status === "failed")    return "color: #c25f5f";
  if (session.status === "completed") return "color: var(--accent); opacity: 0.8";
  return "color: #c8a84b";
}

function pipelineStatusLabel(session) {
  const stages = pipelineStages(session);
  const failed = stages.filter((s) => s.stage?.status === "failed");
  const active = stages.filter((s) => s.stage?.status === "started" || s.stage?.status === "processing");
  if (failed.length)  return `Failed at ${failed.map((s) => s.label).join(", ")}`;
  if (active.length)  return `Processing ${active.map((s) => s.label).join(" + ")}…`;
  if (session.status === "uploaded") return "Queued — waiting to start";
  if (session.status === "completed") return "Completed";
  const done = stages.filter((s) => s.stage?.status === "completed").length;
  return done === 0 ? "Starting…" : `${done} / ${stages.length} stages done`;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function protocolLabel(s) {
  const id = (s.id ?? "").toLowerCase();
  const fn = (s.filename ?? "").toLowerCase();
  if (fn.includes("ssh") || id.includes("ssh")) return "SSH";
  if (fn.includes("vnc") || id.includes("vnc")) return "VNC";
  return "RDP";
}

function formatDate(ts) {
  if (!ts) return "—";
  return new Date(ts * 1000).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function formatDuration(ms) {
  if (!ms) return "—";
  const t = Math.floor(ms / 1000);
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
}

function formatBytes(bytes) {
  if (bytes === null || bytes === undefined || isNaN(bytes)) return "—";
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  let i = Math.floor(Math.log(bytes) / Math.log(k));
  if ((bytes / Math.pow(k, i)) >= 1000 && i < sizes.length - 1) i++;
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

// ─── WebSocket ────────────────────────────────────────────────────────────────
function onWsReload() { loadSessions(); }

function onWsStage(e) {
  const { session_id, stage, status } = e.detail ?? {};
  const idx = sessionStore.sessions.findIndex((s) => s.id === session_id);
  if (idx === -1) return;
  const session = sessionStore.sessions[idx];
  sessionStore.sessions.splice(idx, 1, {
    ...session,
    [stage]: { ...(session[stage] ?? {}), status },
  });
  stageUpdateTick.value++;
}

onMounted(() => {
  loadSessions();
  window.addEventListener("ws:session-uploaded",  onWsReload);
  window.addEventListener("ws:pipeline-stage",    onWsStage);
  window.addEventListener("ws:session-completed", onWsReload);
  window.addEventListener("ws:session-failed",    onWsReload);
});

onUnmounted(() => {
  window.removeEventListener("ws:session-uploaded",  onWsReload);
  window.removeEventListener("ws:pipeline-stage",    onWsStage);
  window.removeEventListener("ws:session-completed", onWsReload);
  window.removeEventListener("ws:session-failed",    onWsReload);
});
</script>

<style scoped>
/* Search */
.search-local-wrapper { position: relative; display: flex; align-items: center; width: 100%; margin-bottom: 1.5rem; }
input[type="text"].search-local-input {
  width: 100%; height: 2.5rem;
  padding: 0.625rem 2.5rem 0.625rem 2.5rem;
  background-color: var(--bg-primary);
  border: 1px solid var(--border-color) !important;
  border-radius: 0.5rem !important;
  font-size: 0.875rem; color: var(--text-primary);
  transition: all 0.2s ease; outline: none !important; box-shadow: none !important;
}
input[type="text"].search-local-input::placeholder { color: var(--text-secondary); }
input[type="text"].search-local-input:focus { border-color: var(--accent) !important; }
.search-local-icon { position: absolute; left: 0.75rem; width: 1rem; height: 1rem; color: var(--text-secondary); pointer-events: none; z-index: 10; }
.search-clear-btn { position: absolute; right: 0.625rem; display: flex; align-items: center; justify-content: center; width: 1.25rem; height: 1.25rem; color: var(--text-secondary); background: transparent; border: none; cursor: pointer; border-radius: 50%; transition: color 0.15s; }
.search-clear-btn:hover { color: var(--text-primary); }

.toolbar-btn { display: flex; align-items: center; justify-content: center; width: 30px; height: 30px; background: transparent; border: none; border-radius: 50%; color: var(--text-secondary); cursor: pointer; transition: background 0.15s; }
.toolbar-btn:hover:not(:disabled) { background: var(--hover-bg); color: var(--text-primary); }
.toolbar-btn:disabled { opacity: 0.3; cursor: default; }

.tab-btn { flex: 0 0 110px; padding: 6px 14px; text-align: center; font-size: 11px; font-weight: 700; letter-spacing: 0.5px; color: var(--text-secondary); background: none; border: none; border-bottom: 2px solid transparent; cursor: pointer; transition: all 0.15s; white-space: nowrap; border-radius: 6px 6px 0 0; }
.tab-btn:hover  { color: var(--text-primary); background: var(--hover-bg); }
.tab-btn.active { color: var(--text-primary); border-bottom-color: var(--accent); background: transparent; }

.table-th { padding: 12px 16px; text-align: left; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-secondary); white-space: nowrap; }
.table-td { padding: 14px 16px; vertical-align: middle; }

.action-btn { display: inline-flex; align-items: center; justify-content: center; width: 26px; height: 26px; border-radius: 6px; border: 1px solid var(--border-color); background: transparent; color: var(--text-secondary); cursor: pointer; transition: all 0.15s ease; }
.action-btn:hover { color: var(--text-primary); background: var(--bg-quaternary); }
.action-btn.danger:hover { color: var(--color-error, #f87171); border-color: var(--color-error, #f87171); background: rgba(248,113,113,0.1); }

.session-tag { display: inline-flex; align-items: center; padding: 2px 6px; font-size: 10px; font-weight: 700; border-radius: 3px; border: 1px solid; text-transform: uppercase; letter-spacing: 0.5px; }
.tag-ssh { background: rgba(78,201,176,.12);  border-color: rgba(78,201,176,.35);  color: #6dd9bf; }
.tag-rdp { background: rgba(86,156,214,.12);  border-color: rgba(86,156,214,.35);  color: #7db3e8; }
.tag-vnc { background: rgba(197,134,192,.12); border-color: rgba(197,134,192,.35); color: #d8a8d3; }

.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.custom-scrollbar { scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.1) transparent; }
.custom-scrollbar::-webkit-scrollbar       { width: 6px; height: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 3px; }
</style>